import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'
import { meQuery } from '@/auth/me'
import { AppShell } from '@/components/app/app-shell'
import { Button } from '@/components/ui/button'
import { isConfigured } from '@/lib/env'
import { setSentryUser } from '@/lib/sentry'
import { supabase } from '@/lib/supabase'
import { onOutboxSent } from '@/offline/outbox'
import { startOutboxSync } from '@/offline/sync'

/** Signed-in area. Works offline once the user has signed in on this device. */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    if (!isConfigured) throw redirect({ to: '/setup' })
    const { data } = await supabase.auth.getSession()
    if (!data.session) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
    const me = await context.queryClient.ensureQueryData(meQuery)
    if (!me) {
      await supabase.auth.signOut()
      context.queryClient.clear()
      throw redirect({ to: '/login', search: { reason: 'inactive' } })
    }
    return { me }
  },
  errorComponent: OfflineFirstSignIn,
  component: AppLayout,
})

function AppLayout() {
  const { me: initial } = Route.useRouteContext()
  const { data: me } = useQuery({ ...meQuery, initialData: initial })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = me ?? initial

  // Outbox items belong to the real signed-in user, never a viewed-as user.
  useEffect(() => startOutboxSync(user.real_user_id), [user.real_user_id])
  useEffect(() => setSentryUser(user.real_user_id), [user.real_user_id])

  // Load the main screens' code in the background, so they open with no signal even before the
  // service worker has finished caching (first visit).
  const router = useRouter()
  useEffect(() => {
    const preload = () => {
      for (const to of ['/reluxes', '/queue', '/tools', '/account', '/notifications'] as const) {
        void router.preloadRoute({ to }).catch(() => undefined)
      }
    }
    const id = window.setTimeout(preload, 1500)
    return () => window.clearTimeout(id)
  }, [router])

  // Refresh lists the moment something queued on this device reaches the server.
  useEffect(
    () =>
      onOutboxSent(() => {
        void queryClient.invalidateQueries({ queryKey: ['reluxes'] })
        void queryClient.invalidateQueries({ queryKey: ['queue'] })
        void queryClient.invalidateQueries({ queryKey: ['relux'] })
        void queryClient.invalidateQueries({ queryKey: ['projects'] })
      }),
    [queryClient],
  )

  // Live queue: the database broadcasts an empty "changed" signal; refetch through the safe RPCs.
  useEffect(() => {
    const channel = supabase
      .channel('queue')
      .on('broadcast', { event: 'changed' }, () => {
        void queryClient.invalidateQueries({ queryKey: ['queue'] })
        void queryClient.invalidateQueries({ queryKey: ['reluxes'] })
      })
      .subscribe()
    return () => {
      void supabase.removeChannel(channel)
    }
  }, [queryClient])

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        queryClient.clear()
        void navigate({ to: '/login' })
      }
    })
    return () => data.subscription.unsubscribe()
  }, [navigate, queryClient])

  return <AppShell me={user} />
}

function OfflineFirstSignIn({ error }: { error: unknown }) {
  return (
    <div className="mx-auto max-w-md p-8 text-center">
      <h1 className="text-3xl uppercase">Almost there</h1>
      <p className="mt-2 text-fg-muted">
        {navigator.onLine
          ? `We could not load your account: ${error instanceof Error ? error.message : String(error)}`
          : 'Connect to signal once to finish signing in. After that MezSites works offline.'}
      </p>
      <Button className="mt-6" onClick={() => window.location.reload()}>
        Try again
      </Button>
    </div>
  )
}
